import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HandshakeIcon from "@mui/icons-material/Handshake";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type SwapProposal,
  acceptSwap,
  cancelSwap,
  createSwapProposal,
  listSwapProposals,
  rejectSwap,
} from "@/api/swaps";

const STATUS_COLOR: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
  PROPOSED: "primary",
  ACCEPTED: "success",
  REJECTED: "error",
  CANCELLED: "default",
  EXPIRED: "default",
};

export function SwapPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const proposals = useQuery({
    queryKey: ["swap-proposals"],
    queryFn: () => listSwapProposals("all"),
    refetchOnWindowFocus: true,
  });

  const accept = useMutation({
    mutationFn: (id: string) => acceptSwap(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["swap-proposals"] });
      qc.invalidateQueries({ queryKey: ["my-planning"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
  const reject = useMutation({
    mutationFn: (id: string) => rejectSwap(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["swap-proposals"] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => cancelSwap(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["swap-proposals"] }),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [myId, setMyId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [msg, setMsg] = useState("");
  const [createErr, setCreateErr] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createSwapProposal(myId.trim(), targetId.trim(), msg.trim() || undefined),
    onSuccess: () => {
      setCreateOpen(false);
      setMyId("");
      setTargetId("");
      setMsg("");
      setCreateErr(null);
      qc.invalidateQueries({ queryKey: ["swap-proposals"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setCreateErr(detail || "error");
    },
  });

  const rows = proposals.data ?? [];
  const incoming = rows.filter((p) => p.direction === "incoming");
  const outgoing = rows.filter((p) => p.direction === "outgoing");

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <HandshakeIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("swaps.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("swaps.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {t("swaps.newProposal")}
          </Button>
        </Stack>

        {proposals.isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Stack spacing={3}>
            <Section
              title={t("swaps.incomingTitle")}
              empty={t("swaps.incomingEmpty")}
              rows={incoming}
              renderActions={(p) =>
                p.status === "PROPOSED" ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => accept.mutate(p.id)}
                      disabled={accept.isPending}
                    >
                      {t("swaps.accept")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => reject.mutate(p.id)}
                      disabled={reject.isPending}
                    >
                      {t("swaps.reject")}
                    </Button>
                  </Stack>
                ) : null
              }
            />
            <Section
              title={t("swaps.outgoingTitle")}
              empty={t("swaps.outgoingEmpty")}
              rows={outgoing}
              renderActions={(p) =>
                p.status === "PROPOSED" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<CancelIcon />}
                    onClick={() => cancel.mutate(p.id)}
                    disabled={cancel.isPending}
                  >
                    {t("swaps.cancel")}
                  </Button>
                ) : null
              }
            />
          </Stack>
        )}
      </CardContent>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("swaps.newProposal")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t("swaps.idsHelp")}</Alert>
            {createErr && <Alert severity="error">{createErr}</Alert>}
            <TextField
              label={t("swaps.myAssignmentId")}
              value={myId}
              onChange={(e) => setMyId(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t("swaps.targetAssignmentId")}
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t("swaps.messageOpt")}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !myId.trim() || !targetId.trim()}
          >
            {t("swaps.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function Section({
  title,
  empty,
  rows,
  renderActions,
}: {
  title: string;
  empty: string;
  rows: SwapProposal[];
  renderActions: (p: SwapProposal) => React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {title} <Chip size="small" label={rows.length} sx={{ ml: 1 }} />
      </Typography>
      {rows.length === 0 ? (
        <Alert severity="info" variant="outlined">
          {empty}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((p) => (
            <Box
              key={p.id}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {p.proposer_display}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.proposer_assignment.date}
                    </Typography>
                    <Typography component="span" sx={{ mx: 0.5 }}>
                      ⇄
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {p.target_display}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.target_assignment.date}
                    </Typography>
                    <Chip
                      size="small"
                      color={STATUS_COLOR[p.status] ?? "default"}
                      label={p.status}
                    />
                  </Stack>
                  {p.message && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
                    >
                      « {p.message} »
                    </Typography>
                  )}
                  {p.decision_reason && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {p.decision_reason}
                    </Typography>
                  )}
                </Box>
                <Box>{renderActions(p)}</Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
